import React, { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";

const CourseForm: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  console.log("CourseForm token:",id);
  const { token } = useAppSelector((state) => state.auth);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [fullDescription, setFullDescription] = useState("");
  const [price, setPrice] = useState<number>(0);
  const [oldPrice, setOldPrice] = useState<number>();
  const [validity, setValidity] = useState("");
  const [languages, setLanguages] = useState<string>("");
  const [buyLink, setBuyLink] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch course for edit
  useEffect(() => {
    if (!id || !token) return;

    const fetchCourse = async () => {
      try {
        const res = await fetch(`${baseUrl}/client/courses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Failed to fetch course");
        const data = await res.json();
        console.log("Fetched course response data:", data.status,data.data);
        if (data?.success && data?.data) {
          const c = data.data;
          console.log("Fetched course data:", c);
          setTitle(c.title || "");
          setSlug(c.slug || "");
          setShortDesc(c.description || "");
          setFullDescription(c.description || "");
          setPrice(c.price || 0);
          setOldPrice(c.oldPrice || undefined);
          setValidity(c.validity || "");
          setLanguages(Array.isArray(c.languages) ? c.languages.join(", ") : c.languages || "");
          setBuyLink(c.buyLink || "");
          if (c.image) setPreviewUrl(c.image.startsWith("http") ? c.image : `${baseUrl}/${c.image}`);
        }
      } catch (err) {
        console.error("❌ Error fetching course:", err);
        alert("Failed to fetch course data.");
      }
    };

    fetchCourse();
  }, [id, baseUrl, token]);

  // Preview image
  useEffect(() => {
    if (!image) return;
    const url = URL.createObjectURL(image);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!id && !image) {
      alert("Please upload a course image!");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("slug", slug);
      formData.append("description", fullDescription || shortDesc);
      formData.append("price", price.toString());
      if (oldPrice) formData.append("oldPrice", oldPrice.toString());
      formData.append("validity", validity);
      formData.append("languages", JSON.stringify(languages.split(",").map(l => l.trim())));
      formData.append("buyLink", buyLink);
      if (image) formData.append("image", image);

      const url = id ? `${baseUrl}/courses/${id}` : `${baseUrl}/courses`;
      const method = id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (res.ok && data?.status) {
        alert(id ? "✅ Course updated!" : "✅ Course added!");
        navigate("/courses");
      } else {
        alert(data?.message || "Something went wrong!");
      }
    } catch (err) {
      console.error("❌ Error submitting course:", err);
      alert("Failed to submit course.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-container container-fluid">
      <div className="page-header mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard">Home</a></li>
          <li className="breadcrumb-item"><a href="/courses">Courses</a></li>
          <li className="breadcrumb-item active">{id ? "Edit Course" : "Add Course"}</li>
        </ol>
        <h1 className="page-title">{id ? "Edit Course" : "Add Course"}</h1>
      </div>

      <form onSubmit={handleSubmit} encType="multipart/form-data" className="card p-4">
        <div className="form-group mb-3">
          <label>Title</label>
          <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} required />
        </div>

        <div className="form-group mb-3">
          <label>Slug</label>
          <input type="text" className="form-control" value={slug} onChange={e => setSlug(e.target.value)} required />
        </div>

        <div className="form-group mb-3">
          <label>Full Description</label>
          <textarea className="form-control" rows={6} value={fullDescription} onChange={e => setFullDescription(e.target.value)} />
        </div>

        <div className="form-group mb-3">
          <label>Price</label>
          <input type="number" className="form-control" value={price} onChange={e => setPrice(Number(e.target.value))} required />
        </div>

        <div className="form-group mb-3">
          <label>Old Price</label>
          <input type="number" className="form-control" value={oldPrice || ""} onChange={e => setOldPrice(Number(e.target.value))} />
        </div>

        <div className="form-group mb-3">
          <label>Validity</label>
          <input type="text" className="form-control" value={validity} onChange={e => setValidity(e.target.value)} />
        </div>

        <div className="form-group mb-3">
          <label>Languages (comma-separated)</label>
          <input type="text" className="form-control" value={languages} onChange={e => setLanguages(e.target.value)} />
        </div>

        <div className="form-group mb-3">
          <label>Buy Link</label>
          <input type="url" className="form-control" value={buyLink} onChange={e => setBuyLink(e.target.value)} />
        </div>

        <div
          style={{ border: "2px dashed #bbb", borderRadius: "12px", padding: "30px", textAlign: "center", cursor: "pointer", background: "#fafafa", marginBottom: "20px" }}
          onClick={() => document.getElementById("fileInput")?.click()}
        >
          {previewUrl ? (
            <div>
              <img src={previewUrl} alt="Preview" style={{ maxHeight: "180px", borderRadius: "8px" }} />
              <button type="button" className="btn btn-sm btn-danger mt-3" onClick={e => { e.stopPropagation(); setImage(null); setPreviewUrl(""); }}>Remove</button>
            </div>
          ) : (
            <>
              <p className="fw-bold mb-1">Drag & Drop your image here</p>
              <p className="text-muted mb-0">or click to browse</p>
            </>
          )}
          <input id="fileInput" type="file" accept="image/*" style={{ display: "none" }} onChange={e => e.target.files && setImage(e.target.files[0])} required={!id} />
        </div>

        <button type="submit" className="btn btn-primary w-100" disabled={loading}>
          {loading ? (id ? "Updating…" : "Uploading…") : id ? "Update" : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default CourseForm;
